export const WORKSPACE_IDS = [
  'SERVICE',
  'KITCHEN',
  'ANALYTICS',
  'INVENTORY',
  'HR',
  'POS',
  'CUSTOMER_HISTORY',
  'MARKETING',
  'MENU',
  'LOYALTY',
] as const;

export type WorkspaceId = (typeof WORKSPACE_IDS)[number];

/** Constructor is a manager-only destination, not an access-policy workspace. */
export type PanelDestinationId = WorkspaceId | 'CONSTRUCTOR';

export type PanelNavigationRole = 'sala' | 'cocina' | 'gerencia';

type WorkspaceGrantProvenance = 'published_membership' | 'derived_assignment' | 'manager_configuration';

interface PanelWorkspaceGrant {
  readonly workspaceId: WorkspaceId;
  readonly provenance: WorkspaceGrantProvenance;
}

/*
 * Internal presentation model, not a wire contract. The later generated
 * Identity adapter must validate and map the canonical response before it can
 * satisfy the runtime port with this value.
 */
export interface PanelAccessModel {
  readonly restaurantId: string;
  readonly subjectUserId: string;
  readonly grants: readonly PanelWorkspaceGrant[];
  readonly defaultRoute: string | null;
  readonly exactPermissions: readonly string[];
  readonly navigationEnabled: boolean;
  /**
   * Presentation profile supplied by the future canonical Identity adapter.
   * It only narrows already-granted destinations; it never grants access.
   */
  readonly navigationRole: PanelNavigationRole;
  /**
   * Derived by the Identity adapter from exact restaurant-manager authority.
   * Constructor is intentionally not one of the ten grantable workspaces.
   */
  readonly constructorEnabled?: boolean;
}

export interface PanelDestination {
  readonly workspaceId: PanelDestinationId;
  readonly label: string;
  readonly shortLabel: string;
  readonly path: string;
  readonly group: PanelDestinationGroup;
}

export const PANEL_DESTINATION_GROUPS = ['Gestión', 'Departamentos', 'Operativas'] as const;

type PanelDestinationGroup = (typeof PANEL_DESTINATION_GROUPS)[number];

/** A group's name on a Board card, where it names the one card's family. */
export const PANEL_DESTINATION_GROUP_CARD_LABELS: Readonly<Record<PanelDestinationGroup, string>> = Object.freeze({
  'Gestión': 'Gestión',
  'Departamentos': 'Departamentos',
  'Operativas': 'Operativa',
});

/* Frozen P0 live overlay. POS stays distinct from SERVICE. */
export const PANEL_DESTINATIONS: readonly PanelDestination[] = Object.freeze([
  { workspaceId: 'CONSTRUCTOR', label: 'Constructor', shortLabel: 'Plano', path: '/app/constructor', group: 'Gestión' },
  { workspaceId: 'ANALYTICS', label: 'Analítica', shortLabel: 'Analítica', path: '/app/analytics', group: 'Departamentos' },
  { workspaceId: 'MARKETING', label: 'Marketing', shortLabel: 'Marketing', path: '/app/promotions', group: 'Departamentos' },
  { workspaceId: 'LOYALTY', label: 'Tarjetas', shortLabel: 'Tarjetas', path: '/app/cards', group: 'Departamentos' },
  { workspaceId: 'INVENTORY', label: 'Inventario', shortLabel: 'Inventario', path: '/app/inventory', group: 'Departamentos' },
  { workspaceId: 'CUSTOMER_HISTORY', label: 'Historial de pedidos', shortLabel: 'Historial', path: '/app/order-history', group: 'Departamentos' },
  { workspaceId: 'HR', label: 'Recursos Humanos', shortLabel: 'RRHH', path: '/app/employees', group: 'Departamentos' },
  { workspaceId: 'MENU', label: 'Menú', shortLabel: 'Menú', path: '/app/menu', group: 'Departamentos' },
  { workspaceId: 'SERVICE', label: 'Servicio', shortLabel: 'Servicio', path: '/app/waiterdelivers', group: 'Operativas' },
  { workspaceId: 'KITCHEN', label: 'Cocina', shortLabel: 'Cocina', path: '/app/dishes', group: 'Operativas' },
  { workspaceId: 'POS', label: 'TPV', shortLabel: 'TPV', path: '/app/orders', group: 'Operativas' },
]);

/*
 * D-160 ruling 20 (parity report C20). v20 declares each restricted role's
 * whole navigation once, in `ROL_SCOPE`
 * (docs/design/panel-v20/census/_logic.js line 294):
 *   Sala   → ['waiter', 'tpv']  = Servicio, TPV
 *   Cocina → ['kitchen']        = Cocina
 * Gerencia has no entry there (`rolIds()` returns null), so management keeps
 * every granted destination; the set below is the manager's compact rail.
 * The bottom bar is not this set: see `bottomBarDestinations`.
 */
const ROLE_DESTINATION_IDS = {
  sala: ['SERVICE', 'POS'],
  cocina: ['KITCHEN'],
  // Home joins this set in P14, when that route exists. P1 must not invent it.
  gerencia: ['ANALYTICS', 'CUSTOMER_HISTORY', 'HR'],
} as const satisfies Readonly<Record<PanelNavigationRole, readonly WorkspaceId[]>>;

const NAVIGATION_ROLE_LABELS = {
  sala: 'Sala',
  cocina: 'Cocina',
  gerencia: 'Gerencia',
} as const satisfies Readonly<Record<PanelNavigationRole, 'Sala' | 'Cocina' | 'Gerencia'>>;

export const navigationRoleLabel = (role: PanelNavigationRole): 'Sala' | 'Cocina' | 'Gerencia' => NAVIGATION_ROLE_LABELS[role];

/**
 * Uses the extract's role-specific compact sets, intersected with canonical
 * grants.
 */
export const roleDestinations = (
  destinations: readonly PanelDestination[],
  role: PanelNavigationRole,
): readonly PanelDestination[] => {
  const grantedByWorkspace = new Map(destinations.map((destination) => [destination.workspaceId, destination] as const));
  return ROLE_DESTINATION_IDS[role].flatMap((workspaceId) => {
    const destination = grantedByWorkspace.get(workspaceId);
    return destination === undefined ? [] : [destination];
  });
};

/**
 * D-160 ruling 20: what a shell *lists*. A restricted role is listed its v20
 * scope and nothing else — in the rail, in the wide column, in the bottom bar
 * and in the «Más» sheet. Management is listed every granted destination, as
 * v20 lists it.
 *
 * This is presentation only. It never narrows `visibleDestinations`, so the
 * grants, `isWorkspaceGranted`, the admission checks in `src/app/**` and the
 * deny-and-return route are untouched: a granted route typed into the address
 * bar still opens, it is simply not listed.
 */
export const navigationScope = (
  destinations: readonly PanelDestination[],
  role: PanelNavigationRole,
): readonly PanelDestination[] => (role === 'gerencia' ? destinations : roleDestinations(destinations, role));

/*
 * The bottom bar of a touch device, as the staff drawings list it
 * (docs/design/panel-v20/mockups/staff-v20/v20.js, `[data-bottomnav]`):
 * TPV, Servicio, Cocina, in that order, for every role (owner ruling of
 * 11 October 2026). Every other destination is under «Más».
 */
const BOTTOM_BAR_DESTINATION_IDS = ['POS', 'SERVICE', 'KITCHEN'] as const satisfies readonly WorkspaceId[];

/**
 * The service destinations a shell lists in its bottom bar: the three of the
 * drawing, cut to what the role is listed (`navigationScope`), so a waiter
 * sees TPV and Servicio, a cook Cocina and a manager all three. Presentation
 * only: a destination that is not granted is never in `destinations`, and
 * admission stays with the route registry.
 */
export const bottomBarDestinations = (
  destinations: readonly PanelDestination[],
  role: PanelNavigationRole,
): readonly PanelDestination[] => {
  const listed = new Map(navigationScope(destinations, role).map((destination) => [destination.workspaceId, destination] as const));
  return BOTTOM_BAR_DESTINATION_IDS.flatMap((workspaceId) => {
    const destination = listed.get(workspaceId);
    return destination === undefined ? [] : [destination];
  });
};

export const visibleDestinations = (access: PanelAccessModel): readonly PanelDestination[] => {
  if (!access.navigationEnabled) return [];
  const allowed = new Set(access.grants.map((grant) => grant.workspaceId));
  return PANEL_DESTINATIONS.filter((destination) =>
    destination.workspaceId === 'CONSTRUCTOR'
      ? access.constructorEnabled === true
      : allowed.has(destination.workspaceId),
  );
};

export const isWorkspaceGranted = (access: PanelAccessModel, workspaceId: PanelDestinationId): boolean =>
  visibleDestinations(access).some((destination) => destination.workspaceId === workspaceId);

/** The server's own reading of the membership's canonical roles: this person runs the restaurant. */
export const runsTheRestaurant = (access: PanelAccessModel): boolean => access.navigationRole === 'gerencia';

export const hasExactPermission = (access: PanelAccessModel, permission: string): boolean =>
  access.exactPermissions.includes(permission);
